import React, { useState, useEffect } from 'react';
import { FaUpload } from "react-icons/fa";
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../utils/axiosInstance';
import { toastSuccess, toastError } from "../../utils/alertHelper";

const IMAGE_BASE_URL = process.env.REACT_APP_Image_URL || "https://api.solarestimate.in/image/";

export default function ItemCreate() {
  const { id: item_id } = useParams();
  const navigate = useNavigate();

  const [itemCode, setItemCode] = useState('');
  const [itemName, setItemName] = useState('');
  const [itemAvailable, setItemAvailable] = useState(false);
  // basic fields
  const [skuCode, setSkuCode] = useState('');
  const [unitOptions, setUnitOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [gstOptions, setGstOptions] = useState([]);
  const [unit, setUnit] = useState('');
  const [group, setGroup] = useState('');
  const [category, setCategory] = useState('');
  const [taxType, setTaxType] = useState('');
  const [intrastateGst, setIntrastateGst] = useState('');
  const [interstateGst, setInterstateGst] = useState('');
  const [salesPrice, setSalesPrice] = useState('');
  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState(null);
  const [statesList, setStatesList] = useState([]);
  const [stateSelections, setStateSelections] = useState({});

  useEffect(() => {
    async function fetchData() {
      try {
        const unitsRes = await axios.get(`/admin/api/item-unit`);
        if (unitsRes.data && unitsRes.data.units) {
          setUnitOptions(unitsRes.data.units);
        }

        // fetch groups, categories and gst/tax list
        try {
          const [groupRes, catRes, gstRes] = await Promise.all([
            axios.get(`/admin/api/item-group`),
            axios.get(`/admin/api/itemCategory`),
            axios.get(`/admin/api/item-tax`),
          ]);
          const groups = (groupRes.data && (groupRes.data.groups || groupRes.data.data || groupRes.data)) || [];
          const categories = (catRes.data && (catRes.data.categories || catRes.data.data || catRes.data)) || [];
          const gstList = (gstRes.data && (gstRes.data.taxes || gstRes.data.gsts || gstRes.data.items || gstRes.data.data)) || [];
          setGroupOptions(groups);
          setCategoryOptions(categories);
          setGstOptions(gstList);
        } catch (e) {
          // ignore optional lists
        }

        if (item_id) {
          const itemRes = await axios.get(`/admin/api/item/${item_id}`);
          if (itemRes.data && itemRes.data.Item) {
            mapFetchedItemToState(itemRes.data.Item);
          }
        }
        // fetch states
        try {
          const statesRes = await axios.get(`/admin/api/state`);
          const states = (statesRes.data && (statesRes.data.states || statesRes.data.data || statesRes.data)) || [];
          setStatesList(states);
          setStateSelections(prev => {
            const next = { ...prev };
            states.forEach(s => {
              if (!next[s._id]) next[s._id] = { checked: false, price: '' };
            });
            return next;
          });
        } catch (e) {

        }
      } catch (err) {
        toastError("Failed to fetch data. Please try again.");
      }
    }
    fetchData();
  }, [item_id]);

  useEffect(() => {
    if (window.bootstrap && window.bootstrap.Tooltip) {
      const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
      tooltipTriggerList.forEach(tooltipTriggerEl => {
        new window.bootstrap.Tooltip(tooltipTriggerEl);
      });
    }
  }, []);

  // Derived GST option lists
  const intrastateOptions = (gstOptions || []).filter(g => g && g.recordType === 'tax_group');
  const interstateOptions = (gstOptions || []).filter(g => g && String(g.gstType || '').toUpperCase() === 'IGST');

  function mapFetchedItemToState(Item) {
    setItemCode(Item.code || '');
    setItemName(Item.name || '');
    setItemAvailable(Item.isAvailable || false);
    setSkuCode(Item.skucode || '');
    setUnit(Item.unit?._id || (Item.unit || ''));
    setGroup(Item.group?._id || (Item.group || ''));
    setCategory(Item.category?._id || (Item.category || ''));
    setTaxType(Item.taxType || '');
    // helper to resolve id from object or return string
    const resolveId = (v) => {
      if (!v && v !== 0) return '';
      if (typeof v === 'object') return v._id || v.id || '';
      return String(v);
    };
    // Map intra/inter state GST to their _id when available
    setIntrastateGst(resolveId(Item.intraStateGst || Item.intrastateGst));
    setInterstateGst(resolveId(Item.interStateGst || Item.interstateGst));
    setSalesPrice(Item.salesPrice?.toString() || '');
    setPrice(Item.price?.toString() || '');
    setDescription(Item.description || '');
    // For edit: map the image URL if exists
    setImage(Item.image ? { url: `${IMAGE_BASE_URL}${Item.image}` } : null);
    // map showState
    const rates = Array.isArray(Item.stateRate) ? Item.stateRate : (Array.isArray(Item.showState) ? Item.showState : []);
    if (rates.length) {
      const selections = {};
      rates.forEach(s => {
        const id = s.stateId || (s.state && (s.state._id || s.state)) || (s._id || '');
        const price = (s.price ?? s.rate ?? s.value ?? '') || '';
        if (id) selections[id] = { checked: true, price: String(price) };
      });
      setStateSelections(prev => ({ ...prev, ...selections }));
    }
  }

  const handleImageChange = (e) => {
    if (e.target.files?.length > 0) {
      const file = e.target.files[0];

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        toastError('Invalid file type. Only JPG, JPEG and PNG files are allowed.');
        e.target.value = ''; 
        return;
      }

      
      const maxSize = 2 * 1024 * 1024; 
      if (file.size > maxSize) {
        toastError('File size exceeds 2MB. Please choose a smaller file.');
        e.target.value = ''; 
        return;
      }

      setImage(file);
    }
  };

  const handleTaxTypeChange = (val) => {
    setTaxType(val);
    if (val !== 'GST') {
      setIntrastateGst('');
      setInterstateGst('');
    }
  };

  const handleStateToggle = (stateId, checked) => {
    setStateSelections(prev => ({
      ...prev,
      [stateId]: { ...(prev[stateId] || { price: '' }), checked }
    }));
  };

  const handleStatePriceChange = (stateId, priceVal) => {
    setStateSelections(prev => ({
      ...prev,
      [stateId]: { ...(prev[stateId] || { checked: false }), price: priceVal }
    }));
  };

  const handleClear = () => {
    setItemCode('');
    setItemName('');
    setItemAvailable(false);
    setSkuCode('');
    setUnit('');
    setGroup('');
    setCategory('');
    setTaxType('');
    setIntrastateGst('');
    setInterstateGst('');
    setSalesPrice('');
    setPrice('');
    setDescription('');
    setImage(null);
    setStateSelections({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Run HTML5 validation and show native messages if invalid
    if (!e.target.checkValidity()) {
      e.target.reportValidity();
      return;
    }

    try {
      const formData = new FormData();
      formData.append('code', itemCode);
      formData.append('name', itemName);
      formData.append('isAvailable', itemAvailable);
      formData.append('skucode', skuCode);
      formData.append('unit', unit);
      formData.append('group', group);
      formData.append('category', category);
      formData.append('taxType', taxType);
      // API expects camel-case keys `intraStateGst` and `interStateGst`
      formData.append('intraStateGst', intrastateGst);
      formData.append('interStateGst', interstateGst);
      formData.append('salesPrice', salesPrice);
      formData.append('description', description);
      if (image && !image.url) formData.append('image', image);
      const showStateArr = Object.keys(stateSelections).filter(id => stateSelections[id].checked).map(id => ({ stateId: id, price: stateSelections[id].price }));
      // Append state rates as form fields like stateRate[0][state]=... and stateRate[0][price]=...
      showStateArr.forEach((s, idx) => {
        formData.append(`stateRate[${idx}][state]`, s.stateId);
        formData.append(`stateRate[${idx}][price]`, s.price);
      });

      if (item_id) {
        await axios.put(`/admin/api/item/${item_id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        toastSuccess('Item updated!');
      } else {
        await axios.post('/admin/api/item', formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        toastSuccess('Item created!');
        handleClear();
      }
      navigate('/admin/item');
    } catch (err) {
      toastError('Failed to submit item.');
    }
  };

  return (
    <div className="container-fluid py-4 px-3">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h2 fw-bold">{item_id ? "Edit Item" : "Add New Item"}</h1>
        <a href="/admin/item" className="text-decoration-none text-secondary">
            &larr; Item
          </a>
      </div>
      <div className="">
        <form
          className="item-form rounded-3 p-4"
          style={{ background: "#F6F6F6", border: "1.5px solid #BBBFD2" }}
          onSubmit={handleSubmit}
        >
          <div className="row g-3 align-items-center mb-3">
            <div className="col-md-2 d-flex align-items-center">
              <div className="form-check form-switch">
                <input className="form-check-input" type="checkbox" id="itemStatus" checked={itemAvailable} onChange={e => setItemAvailable(e.target.checked)} />
                <label className="form-check-label ms-2" htmlFor="itemStatus">Status</label>
              </div>
            </div>
            <div className="col-md-4 d-flex justify-content-end">
              <div>
                <label className="form-label text-muted">Item Image</label>
                <div className="border border-2 border-dashed rounded-3 p-3 bg-light text-center" style={{ width: 260 }}>
                  <label htmlFor="upload-image" className="d-block" style={{ cursor: 'pointer' }}>
                    <FaUpload className="mb-1 text-primary" size={18} />
                    <div style={{ fontSize: 13, color: '#868686' }}>Upload Image</div>
                  </label>
                  <input id="upload-image" type="file" style={{ display: 'none' }} accept=".jpg,.jpeg,.png" onChange={handleImageChange} />
                  <div className="mt-2">
                    {image ? (
                      typeof image === 'object' && image.url ? (
                        <img src={image.url} alt="Preview" className="img-fluid rounded" style={{ maxWidth: 200, maxHeight: 200, objectFit: 'contain' }} />
                      ) : (
                        <img src={URL.createObjectURL(image)} alt="Preview" className="img-fluid rounded" style={{ maxWidth: 200, maxHeight: 200, objectFit: 'contain' }} />
                      )
                    ) : (
                      <div style={{ fontSize: 12, color: '#C0C0C0' }}>No image selected</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr className="mt-2 mb-0 border" />
          <div className="row mb-3 mt-2">
            <div className="col-md-12">
              <div className="row">
                <div className="col-md-2">
                  <label className="form-label text-muted">Item Name <span style={{ color: 'red' }}>*</span></label>
                  <input type="text" className="form-control rounded-3" value={itemName} onChange={e => setItemName(e.target.value)} required />
                </div>
                <div className="col-md-2">
                  <label className="form-label text-muted">Item Code</label>
                  <input type="text" className="form-control rounded-3" value={itemCode} onChange={e => setItemCode(e.target.value)} />
                </div>
                <div className="col-md-2">
                  <label className="form-label text-muted">Unit <span style={{ color: 'red' }}>*</span></label>
                  <select className="form-select rounded-3" value={unit} onChange={e => setUnit(e.target.value)} required>
                    <option value="">Select</option>
                    {unitOptions.map(u => <option key={u._id} value={u._id}>{u.name || u.unitName || u.title}</option>)}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label text-muted">SKU Code <span style={{ color: 'red' }}>*</span></label>
                  <input type="text" className="form-control rounded-3" value={skuCode} onChange={e => setSkuCode(e.target.value)} required />
                </div>
              </div>
            </div>
            
          </div>
          <hr className="mt-2 mb-0 border" />
          <div className="row mb-3 mt-2">
                <div className="col-md-2">
                  <label className="form-label text-muted">Group</label>
                  <select className="form-select rounded-3" value={group} onChange={e => setGroup(e.target.value)}>
                    <option value="">Select</option>
                    {groupOptions.map(g => <option key={g._id} value={g._id}>{g.name || g.groupName || g.title}</option>)}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label text-muted">Category</label>
                  <select className="form-select rounded-3" value={category} onChange={e => setCategory(e.target.value)}>
                    <option value="">Select</option>
                    {categoryOptions.map(c => <option key={c._id} value={c._id}>{c.name || c.categoryName || c.title}</option>)}
                  </select>
                </div>
            </div>
          <hr className="mt-2 mb-0 border" />
          <div className="row mb-3 mt-2">
            <div className="col-md-2">
              <label className="form-label text-muted">Tax Type</label>
              <select className="form-select rounded-3" value={taxType} onChange={e => handleTaxTypeChange(e.target.value)}>
                <option value="">Select</option>
                <option value="GST">GST</option>
                <option value="Non-GST">Non-GST</option>
              </select>
            </div>
            {taxType === 'GST' && (
              <>
                <div className="col-md-2">
                  <label className="form-label text-muted">Intrastate GST</label>
                  <select className="form-select rounded-3" value={intrastateGst} onChange={e => setIntrastateGst(e.target.value)}>
                    <option value="">Select</option>
                    {intrastateOptions.map(g => {
                      const name = (g.name || g.gstName || '');
                      const pct = g.percentage || g.rate || '';
                      const pctLabel = pct ? `@${pct}${String(pct).toString().includes('%') ? '' : '%'}` : '';
                      return <option key={g._id} value={g._id}>{`${name}${pctLabel}`}</option>;
                    })}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label text-muted">Interstate GST</label>
                  <select className="form-select rounded-3" value={interstateGst} onChange={e => setInterstateGst(e.target.value)}>
                    <option value="">Select</option>
                    {interstateOptions.map(g => {
                      const name = (g.name || g.gstName || '');
                      const pct = g.percentage || g.rate || '';
                      const pctLabel = pct ? `@${pct}${String(pct).toString().includes('%') ? '' : '%'}` : '';
                      return <option key={g._id} value={g._id}>{`${name}${pctLabel}`}</option>;
                    })}
                  </select>
                </div>
              </>
            )}
          </div>
           <hr className="mt-2 mb-0 border" />
          <div className="row mb-3 mt-2">
            <div className="col-md-2">
              <label className="form-label text-muted">Sales Price</label>
            </div>
            <div className="col-md-2">
              <label className="form-label text-muted">Price(₹) <span style={{ color: 'red' }}>*</span></label>
              <input
                type="number"
                className="form-control rounded-3"
                value={salesPrice}
                onChange={e => setSalesPrice(e.target.value)}
                required
                min="0"
                step="0.01"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label text-muted">Description</label>
              <textarea className="form-control rounded-3" value={description} onChange={e => setDescription(e.target.value)} rows={3} />
            </div>
          </div>
          <hr className="mt-2 mb-0 border" />
          <div className="mt-3 p-3 rounded-3 border mb-2 bg-white">
            <h6 className="mb-3">Show State</h6>
            <div className="row">
              {statesList.map(st => {
                const sel = stateSelections[st._id] || { checked: false, price: '' };
                return (
                  <div key={st._id} className="col-md-3 mb-2 d-flex align-items-center">
                    <div className="form-check me-2">
                      <input className="form-check-input" type="checkbox" id={`state-${st._id}`} checked={!!sel.checked} onChange={e => handleStateToggle(st._id, e.target.checked)} />
                      <label className="form-check-label ms-2" htmlFor={`state-${st._id}`}>{st.name || st.stateName || st.title}</label>
                    </div>
                    <div style={{ width: 100 }} className="ms-2">
                      <input type="text" className="form-control form-control-sm" placeholder="₹ Price" value={sel.price} onChange={e => handleStatePriceChange(st._id, e.target.value)} disabled={!sel.checked} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClear}
            >
              Clear
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-lg"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}